var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { nanoid } from 'nanoid';
import { toPairs } from 'ramda';
export class BridgeHostService {
    constructor(config, pubSub) {
        this.config = config;
        this.pubSub = pubSub;
        this.instances = new Map();
        this.instancesParams = new Map();
        this.dialogs = new Map();
        // tslint:disable-next-line:no-empty
        this.stopMessageProxy = () => { };
        // xbpm instance url
        this.getInstanceUrl = (instanceId, params) => {
            const _a = params || {}, { baseUrl } = _a, urlParams = __rest(_a, ["baseUrl"]);
            let url = `${baseUrl || this.config.baseUrl || '/'}?`;
            const query = toPairs(Object.assign(Object.assign({}, (urlParams || {})), { instanceId }))
                .map(([k, v]) => `${k}=${v}`)
                .join('&');
            url = `${url}${query}`;
            return encodeURI(url);
        };
        // handle messages
        this.startMessageProxy = () => {
            return this.pubSub.subscribe('*', event => {
                const { payload } = event;
                if (!payload) {
                    // console.warn('[BRIDGE HOST]: Event has no payload', event);
                    return;
                }
                if (payload.targetId === 'root') {
                    this.handleMessage(event);
                }
                else {
                    const target = this.instances.get(payload.targetId);
                    if (target && target !== window) {
                        this.pubSub.publish(event.type, payload, target);
                    }
                }
            });
        };
        // emit event to all instances
        this.broadcast = (type, payload) => {
            this.instances.forEach((it, targetId) => {
                try {
                    this.pubSub.publish(type, Object.assign(Object.assign({}, payload), { targetId }), it);
                }
                catch (e) {
                    console.warn('[BRIDGE HOST]: Cannot broadcast message', e);
                }
            });
        };
        this.handleMessage = (event) => {
            const { payload, source } = event;
            switch (event.type) {
                case 'ready': {
                    const entry = Array.from(this.instances.entries()).find(([id, s]) => s === source && id !== payload.sourceId);
                    if (!entry) {
                        return this.instances.set(payload.sourceId, source);
                    }
                    // restore instance
                    const [instanceId, target] = entry;
                    this.pubSub.publish('restore', Object.assign({ instanceId }, (this.instancesParams.get(instanceId) || {})), target);
                    return;
                }
                case 'resize': {
                    const { height, sourceId } = payload;
                    if (!this.dialogs.get(sourceId)) {
                        try {
                            const iframe = document.querySelector(`iframe#${sourceId}`);
                            if (iframe && iframe.style.height !== '100%' && iframe.style.height !== '100vh') {
                                iframe.style.height = `${height}px`;
                            }
                        }
                        catch (_a) {
                            return;
                        }
                    }
                    return;
                }
                case 'location':
                    const { url, blank } = payload;
                    if (blank) {
                        return window.open(url, '_blank');
                    }
                    return (location.href = url);
                case 'reload':
                    return location.reload();
                case 'open': {
                    const { sourceId, title, instanceId = nanoid(), modal, params } = payload;
                    const extra = Object.assign(Object.assign({}, params), { openerId: sourceId });
                    if (modal) {
                        this.openModal(instanceId, Object.assign({ title }, (extra || {})));
                    }
                    else {
                        this.openPopup(instanceId, extra);
                    }
                    this.instancesParams.set(instanceId, extra);
                    return;
                }
                case 'close': {
                    const { instanceId } = payload;
                    this.closeInstance(instanceId);
                    this.removeInstance(instanceId);
                    this.broadcast('closed', payload);
                    return;
                }
                case 'unload': {
                    const { sourceId } = payload;
                    this.removeInstance(sourceId);
                    this.broadcast('unloaded', payload);
                    return;
                }
                default: {
                    console.warn('Unknown event', payload, source);
                }
            }
        };
        this.createIframeElement = (id, src, container, autoHeight, maxHeight) => {
            const iframe = document.querySelector(`iframe#${id}`) || document.createElement('iframe');
            iframe.setAttribute('id', id);
            iframe.setAttribute('sandbox', 'allow-same-origin allow-top-navigation allow-forms allow-scripts allow-downloads allow-popups allow-popups-to-escape-sandbox');
            iframe.setAttribute('src', src);
            iframe.style.border = '0';
            iframe.style.width = '100%';
            iframe.style.height = autoHeight ? '0px' : '100%';
            iframe.style.maxHeight = maxHeight || this.config.iframeMaxHeight || 'none';
            container.append(iframe);
            return iframe;
        };
        // xbpm iframe
        this.createIframe = (id, parent, params, autoHeight = true, maxHeight) => {
            this.instancesParams.set(id, params);
            const container = typeof parent === 'string' ? document.querySelector(parent) : parent;
            const src = this.getInstanceUrl(id, params);
            this.createIframeElement(id, src, container, autoHeight, maxHeight);
        };
        this.createIframeInPlace = (id, params, autoHeight = false) => {
            const src = params.url || this.getInstanceUrl(id, params);
            const root = document.getElementById('root');
            const container = document.getElementById('portal');
            const iframe = this.createIframeElement(id, src, container, autoHeight);
            // backward compatibility
            iframe.style.height = '100vh';
            root.style.display = 'none';
        };
        this.destroyIframe = (id) => {
            const iframe = document.querySelector(`iframe#${id}`);
            if (iframe) {
                iframe.parentNode.removeChild(iframe);
            }
        };
        // xbpm popup window
        this.openPopup = (id, params) => {
            return window.open(this.getInstanceUrl(id, params), '_blank');
        };
        this.closePopup = (id) => {
            const popup = this.instances.get(id);
            if (popup && popup.opener) {
                popup.close();
            }
        };
        this.closeModal = (id) => {
            const dialog = this.dialogs.get(id);
            if (dialog) {
                dialog.hide();
            }
        };
        this.unloadAll = () => this.instances.forEach((_, targetId) => {
            this.closeInstance(targetId);
            this.removeInstance(targetId);
        });
        //
        this.closeInstance = (instanceId) => {
            if (this.dialogs.has(instanceId)) {
                this.closeModal(instanceId);
            }
            else {
                this.closePopup(instanceId);
            }
        };
        this.removeInstance = (instanceId) => {
            this.instances.delete(instanceId);
            this.instancesParams.delete(instanceId);
        };
        const { unsubscribe } = this.startMessageProxy();
        this.stopMessageProxy = unsubscribe;
    }
    openModal(id, params, config) {
        throw new Error('Method BridgeHostService.openModal not implemented!');
    }
    destroy() {
        this.unloadAll();
        this.stopMessageProxy();
    }
}
//# sourceMappingURL=bridge-host.service.js.map